import type { CheckEarningType } from "../integrations/check/check-earning-type";

/** Granularity level - whether the ledger entry is cut by this dimension */
export const GRANULARITY_LEVELS = [
  "job",
  "department",
  "activity",
  "team_member",
  "cost_type",
  "classification",
  "earning_type",
  "accrual_date",
  "location",
  "work_order",
  "class",
  "equipment",
  "custom_field",
  "company_entity",
] as const;

export type GranularityLevel = (typeof GRANULARITY_LEVELS)[number];

/** These line types are for payroll and also can have different granularity levels assigned */
export const PAYROLL_GRANULARITY_LINE_TYPES = [
  "payroll_funds_source",
  "tax_receivables",
  "net_pay_correction",
  "net_pay_receivables",
  "paper_check_liabilities",
  "employee_earnings",
  "contractor_earnings",
  "employer_benefit_contributions",
  "benefit_liabilities",
  "employer_payroll_taxes",
  "tax_liabilities",
  "child_support_liabilities",
  "prepaid_child_support",
  "misc_ptds",
  "fringe_contributions",
  "fringe_liabilities",
  "workers_comp_expenses",
  "workers_comp_liabilities",
  "burden_rate_expenses",
  "burden_rate_offset",
] as const;
type PayrollGranularityLineType = (typeof PAYROLL_GRANULARITY_LINE_TYPES)[number];

/** These expense mgmt line types can have different granularity levels assigned */
export const EXPENSE_MGMT_GRANULARITY_LINE_TYPES = [
  "reimbursements",
  "unpaid_reimbursement_liabilities",
  "reimbursement_taxes",
  "card_transactions",
  "reimbursement_payout",
  "reimbursement_payment",
  "unpaid_bill_liabilities",
] as const;
type ExpenseMgmtGranularityLineType = (typeof EXPENSE_MGMT_GRANULARITY_LINE_TYPES)[number];

export const EQUIPMENT_GRANULARITY_LINE_TYPES = ["equipment_expense", "equipment_offset"] as const;
type EquipmentGranularityLineType = (typeof EQUIPMENT_GRANULARITY_LINE_TYPES)[number];

export const MATERIAL_LOG_GRANULARITY_LINE_TYPES = ["material_log_expense", "material_log_offset"] as const;
type MaterialLogGranularityLineType = (typeof MATERIAL_LOG_GRANULARITY_LINE_TYPES)[number];

export const AR_GRANULARITY_LINE_TYPES = [
  "invoice_revenue",
  "accounts_receivable",
  "bad_debt_expense",
  "invoice_payment_receipt",
] as const;
type ArGranularityLineType = (typeof AR_GRANULARITY_LINE_TYPES)[number];

export const GRANULARITY_LINE_TYPES = [
  ...PAYROLL_GRANULARITY_LINE_TYPES,
  ...EXPENSE_MGMT_GRANULARITY_LINE_TYPES,
  ...EQUIPMENT_GRANULARITY_LINE_TYPES,
  ...MATERIAL_LOG_GRANULARITY_LINE_TYPES,
  ...AR_GRANULARITY_LINE_TYPES,
] as const;

export type GranularityLineType =
  | PayrollGranularityLineType
  | ExpenseMgmtGranularityLineType
  | EquipmentGranularityLineType
  | MaterialLogGranularityLineType
  | ArGranularityLineType;

export type DefaultLedgerMappingLineType = GranularityLineType;

/** Runtime list of the line types that key a ledger mapping's `defaults` map. Same members as
 *  `DefaultLedgerMappingLineType`; use it to build validators (e.g. Rex's ledger mapping tools). */
export const DEFAULT_LEDGER_MAPPING_LINE_TYPE_VALUES = GRANULARITY_LINE_TYPES;

export type LedgerGranularityConfig = Partial<Record<GranularityLineType, GranularityLevel[]>>;

export type LedgerMappingLineType = CheckEarningType | DefaultLedgerMappingLineType;
